import { mapValues } from 'es-toolkit';
import { useMemo } from 'react';
import { useAppServices } from 'src/common/providers/AppServicesProvider';
import { type StoreApi, useStore } from 'zustand';
import type { IStores } from './types';

export type ReactStores = { [Name in keyof IStores]: IStores[Name]['use'] };

/**
 * The field hooks of every store in the services: `const { playerStore } = useStores();
 * playerStore.metadata()`. For a derived value, use `useStore` with the store from
 * `useAppServices().stores`.
 */
export function useStores(): ReactStores {
  const { stores } = useAppServices();

  return useMemo(() => mapValues(stores, (store) => store.use) as ReactStores, [stores]);
}

type StoreState<Name extends keyof IStores> = ReturnType<IStores[Name]['getState']>;

/**
 * Subscribes to a value derived from a store's state, and re-renders only when that value changes.
 * Prefer this over `useStores()` when many instances of a component each care about a small,
 * instance-specific slice of shared state (e.g. whether this tile is the one playing).
 */
export function useStoreSelector<Name extends keyof IStores, Selected>(
  name: Name,
  selector: (state: StoreState<Name>) => Selected,
): Selected {
  const { stores } = useAppServices();

  return useStore(stores[name] as unknown as StoreApi<StoreState<Name>>, selector);
}
